import React, { useState, useCallback } from 'react';
import { Upload, BarChart3, Users, DollarSign, TrendingUp, FileText, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const VendingAnalyticsApp = () => {
  const [currentView, setCurrentView] = useState('upload');
  const [salesData, setSalesData] = useState([]);
  const [uploadStatus, setUploadStatus] = useState('');
  const [selectedMember, setSelectedMember] = useState('all');
  const [dateRange, setDateRange] = useState('all');

  // Sample data for demonstration
  const sampleData = [
    { memberId: 'VM001', productSku: 'COKE001', productName: 'Coca Cola 12oz', unitPrice: 1.50, quantity: 120, discountLevel: 0.05, month: '2024-01', revenue: 171.00 },
    { memberId: 'VM001', productSku: 'HERS001', productName: 'Hersheys Bar', unitPrice: 2.00, quantity: 85, discountLevel: 0.10, month: '2024-01', revenue: 153.00 },
    { memberId: 'VM002', productSku: 'COKE001', productName: 'Coca Cola 12oz', unitPrice: 1.50, quantity: 95, discountLevel: 0.08, month: '2024-01', revenue: 131.10 },
    { memberId: 'VM002', productSku: 'LAYS001', productName: 'Lays Classic', unitPrice: 1.75, quantity: 110, discountLevel: 0.12, month: '2024-01', revenue: 169.40 },
    { memberId: 'VM003', productSku: 'PEPSI001', productName: 'Pepsi 12oz', unitPrice: 1.45, quantity: 130, discountLevel: 0.06, month: '2024-01', revenue: 177.22 },
    { memberId: 'VM001', productSku: 'COKE001', productName: 'Coca Cola 12oz', unitPrice: 1.50, quantity: 135, discountLevel: 0.05, month: '2024-02', revenue: 192.38 },
    { memberId: 'VM002', productSku: 'LAYS001', productName: 'Lays Classic', unitPrice: 1.75, quantity: 125, discountLevel: 0.12, month: '2024-02', revenue: 192.50 },
    { memberId: 'VM003', productSku: 'PEPSI001', productName: 'Pepsi 12oz', unitPrice: 1.45, quantity: 140, discountLevel: 0.06, month: '2024-02', revenue: 190.68 },
  ];

  const processCSVData = useCallback((csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',');
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index]?.trim();
        });
        
        // Calculate revenue
        const unitPrice = parseFloat(row.unitPrice || 0);
        const quantity = parseInt(row.quantity || 0);
        const discount = parseFloat(row.discountLevel || 0);
        row.revenue = unitPrice * quantity * (1 - discount);
        
        data.push(row);
      }
    }
    return data;
  }, []);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadStatus('Processing...');
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvData = processCSVData(e.target.result);
        setSalesData([...salesData, ...csvData]);
        setUploadStatus('File uploaded successfully!');
        setTimeout(() => setUploadStatus(''), 3000);
      } catch (error) {
        setUploadStatus('Error processing file. Please check format.');
      }
    };
    
    reader.readAsText(file);
  };

  const loadSampleData = () => {
    setSalesData(sampleData);
    setUploadStatus('Sample data loaded successfully!');
    setTimeout(() => setUploadStatus(''), 3000);
  };

  // Analytics calculations
  const currentData = salesData.length > 0 ? salesData : sampleData;
  
  const filteredData = currentData.filter(item => {
    if (selectedMember !== 'all' && item.memberId !== selectedMember) return false;
    return true;
  });

  const totalRevenue = filteredData.reduce((sum, item) => sum + item.revenue, 0);
  const totalQuantity = filteredData.reduce((sum, item) => sum + parseInt(item.quantity), 0);
  const uniqueMembers = [...new Set(currentData.map(item => item.memberId))];
  const avgDiscount = filteredData.reduce((sum, item) => sum + parseFloat(item.discountLevel), 0) / filteredData.length;

  // Chart data
  const monthlyRevenue = currentData.reduce((acc, item) => {
    const month = item.month;
    acc[month] = (acc[month] || 0) + item.revenue;
    return acc;
  }, {});

  const monthlyData = Object.entries(monthlyRevenue).map(([month, revenue]) => ({
    month,
    revenue: Math.round(revenue)
  }));

  const memberPerformance = currentData.reduce((acc, item) => {
    acc[item.memberId] = (acc[item.memberId] || 0) + item.revenue;
    return acc;
  }, {});

  const memberData = Object.entries(memberPerformance).map(([member, revenue]) => ({
    member,
    revenue: Math.round(revenue)
  }));

  const productPerformance = currentData.reduce((acc, item) => {
    const key = item.productName || item.productSku;
    acc[key] = (acc[key] || 0) + item.revenue;
    return acc;
  }, {});

  const productData = Object.entries(productPerformance)
    .map(([product, revenue]) => ({ product, revenue: Math.round(revenue) }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const renderUploadView = () => (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <Upload className="w-6 h-6 mr-2" />
          Upload Sales Data
        </h2>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg mb-2">Drop your sales files here or click to browse</p>
            <p className="text-sm text-gray-500">Supports CSV, Excel files</p>
          </label>
        </div>

        <div className="text-center mb-6">
          <button
            onClick={loadSampleData}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Load Sample Data
          </button>
        </div>

        {uploadStatus && (
          <div className={`p-4 rounded-lg flex items-center ${
            uploadStatus.includes('Error') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
          }`}>
            {uploadStatus.includes('Error') ? 
              <AlertCircle className="w-5 h-5 mr-2" /> : 
              <CheckCircle className="w-5 h-5 mr-2" />
            }
            {uploadStatus}
          </div>
        )}

        <div className="mt-8 bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold mb-4">Expected File Format:</h3>
          <div className="bg-white rounded border p-4 font-mono text-sm">
            <div className="text-gray-600 mb-2">CSV Header Row:</div>
            <div>memberId,productSku,productName,unitPrice,quantity,discountLevel,month</div>
            <div className="text-gray-600 mt-4 mb-2">Example Data:</div>
            <div>VM001,COKE001,Coca Cola 12oz,1.50,120,0.05,2024-01</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold">${totalRevenue.toFixed(2)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Units Sold</p>
              <p className="text-2xl font-bold">{totalQuantity.toLocaleString()}</p>
            </div>
            <BarChart3 className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Members</p>
              <p className="text-2xl font-bold">{uniqueMembers.length}</p>
            </div>
            <Users className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Discount</p>
              <p className="text-2xl font-bold">{(avgDiscount * 100).toFixed(1)}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium mb-1">Member Filter:</label>
            <select 
              value={selectedMember} 
              onChange={(e) => setSelectedMember(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All Members</option>
              {uniqueMembers.map(member => (
                <option key={member} value={member}>{member}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date Range:</label>
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All Time</option>
              <option value="2024-01">January 2024</option>
              <option value="2024-02">February 2024</option>
            </select>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Monthly Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
              <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Member Performance</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={memberData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="member" />
              <YAxis />
              <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Top Products by Revenue</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={productData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="revenue"
                label={({product, revenue}) => `${product}: $${revenue}`}
              >
                {productData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Sales Data Table</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Member</th>
                  <th className="text-left p-2">Product</th>
                  <th className="text-left p-2">Quantity</th>
                  <th className="text-left p-2">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, 8).map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-2">{item.memberId}</td>
                    <td className="p-2">{item.productName || item.productSku}</td>
                    <td className="p-2">{item.quantity}</td>
                    <td className="p-2">${item.revenue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">Vending Analytics Platform</h1>
            <nav className="flex space-x-4">
              <button
                onClick={() => setCurrentView('upload')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  currentView === 'upload' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Upload Data
              </button>
              <button
                onClick={() => setCurrentView('dashboard')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  currentView === 'dashboard' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BarChart3 className="w-4 h-4 inline mr-2" />
                Dashboard
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {currentView === 'upload' ? renderUploadView() : renderDashboard()}
      </main>
    </div>
  );
};

export default VendingAnalyticsApp;
